import { crossingPreview, getCrossingCrossingListUrl, useCrossingRun } from "@api/generated/crossing/crossing";
import { useMeasurementsOverview } from "@api/generated/measurements/measurements";
import type { CrossingDraft, CrossingIn, CrossingRun, Parent } from "@api/generated/model";
import { useUserMe } from "@api/generated/user/user";
import { apiDownload } from "@api/http";
import { Icon } from "@app/icons";
import { usePreview, useRunResult } from "@app/preview";
import { useInvalidate } from "@app/queryClient";
import { SeasonMissing } from "@app/SeasonMissing";
import { hasRole, useAutosave, useCreateSeason, useSeasonFromLink, useSettings } from "@app/workspace";
import { ResultBanner } from "@features/system/undo";
import { CrossRemovalModal, CrossTable, PairMatrix, ParentList } from "@features/trials/CrossingParts";
import {
  allPairs,
  CROSS_AIMS,
  type CrossingSpec,
  emptySpec,
  plotKey,
  type Side,
  specOf,
  toggledPair,
  withParent,
  withParents,
} from "@features/trials/crossing";
import { hint } from "@i18n";
import { ActionIcon, Autocomplete, Button, Checkbox, Input, Loader, Popover, SegmentedControl, Select, TextInput } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ActionMenu } from "@ui/ActionMenu";
import { formatNumber, shortTime } from "@ui/format";
import { notifyError } from "@ui/notify";
import { PageHeader } from "@ui/PageHeader";
import { QueryFeedback } from "@ui/QueryFeedback";
import { RunBar } from "@ui/RunBar";
import { Splitter } from "@ui/Splitter";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const CHANGED = /crossing-preview|measurements/; // what a run changes: the plan, the trials

export const Route = createFileRoute("/season/crossing")({
  validateSearch: z.object({ ev: z.number().int().optional() }),
  component: CrossingPage,
});

/** Tenyészidőszak → Keresztezés (the legacy Crossing): mothers × fathers of the working season become new
 *  combinations (pedigree, PAz, cross code) sown in a crossing trial of the next year. The plan is kept with the
 *  user's settings; creating the crosses is one undoable operation. */
function CrossingPage() {
  const { t } = useTranslation();
  const canCross = hasRole(useUserMe().data?.roles, "nemesito");
  const ev = useSeasonFromLink(Route.useSearch().ev);
  const settings = useSettings();
  if (!canCross) {
    return (
      <div className="page">
        <PageHeader title={t("crossing.title")} />
        <p className="mu">{t("crossing.breederOnly")}</p>
      </div>
    );
  }
  if (!settings || !ev) return <Loader size="sm" m="md" />;
  const fallback = emptySpec(settings.default_pgm, settings.default_gh);
  return <CrossingPlan key={ev} ev={ev} stored={settings.crossing_draft ?? null} fallback={fallback} />;
}

function CrossingPlan({ ev, stored, fallback }: { ev: number; stored: CrossingDraft | null; fallback: CrossingSpec }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [init] = useState(() => (stored?.ev === ev ? stored : null));
  const [spec, setSpec] = useState<CrossingSpec>(() => specOf(init, fallback));
  const draft = useMemo<CrossingDraft>(() => ({ ev, spec: spec as never }), [ev, spec]);
  const { savedAt } = useAutosave("crossing_draft", draft, init);
  // what the last run created, shown while the plan is the one it left (any change of the plan hides it)
  const { result, keep } = useRunResult<CrossingRun>(spec);
  const patch = (p: Partial<CrossingSpec>) => setSpec((s) => ({ ...s, ...p }));
  const toggleParent = (side: Side, p: Parent, on: boolean) => setSpec((s) => withParent(s, side, p, on));
  const addParents = (side: Side, found: Parent[]) => setSpec((s) => withParents(s, side, found));
  const changed = useInvalidate(CHANGED);
  const targetTrials = (useMeasurementsOverview(ev + 1, { query: { staleTime: 60_000 } }).data ?? []).map((o) => o.kis);
  const seasonTrials = (useMeasurementsOverview(ev, { query: { staleTime: 60_000 } }).data ?? [])
    .filter((o) => o.register_plots > 0)
    .map((o) => o.kis);
  const [removing, setRemoving] = useState(false);

  // what the preview depends on (the growth habit and programme of the new lines only go into the run)
  const body = useMemo(() => {
    const plots = new Map([...spec.mothers, ...spec.fathers].map((p) => [plotKey(p), { kis: p.kis, parc: p.parc }]));
    const pairs = spec.pairs.flatMap(([m, f]) => {
      const mother = plots.get(m);
      const father = plots.get(f);
      return mother && father ? [{ mother, father }] : [];
    });
    if (!spec.target.trim() || !spec.aim || !pairs.length) return null;
    return { ev, target: spec.target, aim: spec.aim, repeat_existing: spec.repeat_existing, pairs };
  }, [ev, spec.mothers, spec.fathers, spec.pairs, spec.target, spec.aim, spec.repeat_existing]);
  const preview = usePreview("crossing-preview", body, (b, signal) => crossingPreview(b as CrossingIn, { signal }));
  const plan = body ? preview.data : undefined;
  const codes = plan?.rows.filter((r) => r.code).map((r) => r.code) ?? [];
  const nextPaz = plan?.rows.find((r) => r.status === "new")?.paz;
  const season = useCreateSeason(() => void client.invalidateQueries({ queryKey: ["crossing-preview"] }));
  const run = useCrossingRun({
    mutation: {
      onSuccess: (out) => {
        const next = { ...spec, pairs: [] };
        setSpec(next);
        keep(out, next);
        changed();
      },
    },
  });
  const download = () =>
    apiDownload(getCrossingCrossingListUrl({ ev: ev + 1, kis: spec.target.trim() }), { method: "GET" }, t("crossing.fileName")).catch(
      notifyError,
    );

  return (
    <div className="page fill">
      <PageHeader
        title={t("crossing.title")}
        season
        subtitle={t("crossing.subtitle")}
        actions={
          <div className="hstack" style={{ gap: 10 }}>
            {savedAt && <span className="sm mu">{t("crossing.draftSaved", { time: shortTime(savedAt.toISOString()) })}</span>}
            <ActionMenu
              size="xs"
              actions={[
                { label: t("crossing.exportList"), icon: "download", onClick: download, hidden: !spec.target.trim() },
                {
                  label: t("crossing.removePlots"),
                  icon: "close",
                  onClick: () => setRemoving(true),
                  hidden: !spec.target.trim(),
                  danger: true,
                },
              ]}
            />
          </div>
        }
      />
      {removing && <CrossRemovalModal ev={ev + 1} kis={spec.target.trim()} onClose={() => setRemoving(false)} />}
      <QueryFeedback busy={preview.busy} error={preview.error} retry={preview.refetch} label={t("async.preview")} />
      <section className="card cr-rules">
        <Autocomplete
          id="cr-target"
          w={150}
          size="xs"
          label={t("crossing.trial", { ev: ev + 1 })}
          data={targetTrials}
          value={spec.target}
          onChange={(v) => patch({ target: v.toUpperCase() })}
          maxLength={40}
        />
        <Select
          id="cr-aim"
          w={320}
          size="xs"
          label={t("crossing.targetLetter")}
          placeholder={hint("searchSelect")}
          searchable
          data={CROSS_AIMS.map((a) => ({ value: a, label: t("crossing.aimOption", { letter: a, meaning: t(`crossing.aims.${a}`) }) }))}
          value={spec.aim || null}
          onChange={(v) => patch({ aim: CROSS_AIMS.find((a) => a === v) ?? "" })}
        />
        <Input.Wrapper size="xs" label={t("crossing.growthHabit")}>
          <SegmentedControl
            size="xs"
            display="flex"
            value={spec.gh}
            onChange={(v) => patch({ gh: v as "W" | "S" })}
            data={[
              { value: "W", label: t("crossing.winter") },
              { value: "S", label: t("crossing.spring") },
            ]}
          />
        </Input.Wrapper>
        <TextInput
          w={90}
          size="xs"
          label="PGM"
          maxLength={10}
          value={spec.pgm}
          onChange={(e) => patch({ pgm: e.currentTarget.value.toUpperCase() })}
        />
        <Checkbox
          size="xs"
          label={t("crossing.repeatPedigree")}
          checked={spec.repeat_existing}
          onChange={(e) => patch({ repeat_existing: e.currentTarget.checked })}
        />
        <div className="grow" />
        {plan && (
          <span className="sm mu" style={{ textAlign: "right" }}>
            {nextPaz != null && (
              <>
                {t("crossing.nextPaz")} <b className="mono">{nextPaz}</b>
              </>
            )}
          </span>
        )}
      </section>
      {plan && !plan.season_exists && <SeasonMissing ev={ev + 1} creating={season.pending} onCreate={() => season.create(ev + 1)} />}

      <div className="fill-row">
        <aside className="card cr-parents">
          {(["mothers", "fathers"] as const).map((side) => (
            <ParentList
              key={side}
              ev={ev}
              side={side}
              parents={spec[side]}
              trials={seasonTrials}
              onToggle={toggleParent}
              onAddMany={addParents}
            />
          ))}
        </aside>
        <Splitter cssVar="--cr-parents-w" side="start" min={260} max={600} />
        <section className="card pad cr-plan">
          <div className="hstack" style={{ gap: 6 }}>
            <h2 className="h2 grow">{t("crossing.plan")}</h2>
            <Button size="compact-xs" variant="default" onClick={() => patch({ pairs: allPairs(spec) })}>
              {t("crossing.allByAll")}
            </Button>
            <Popover width={300} position="bottom-end" withArrow>
              <Popover.Target>
                <ActionIcon size="sm" variant="default" radius="xl" aria-label={t("crossing.purdy")} title={t("crossing.purdy")}>
                  ?
                </ActionIcon>
              </Popover.Target>
              <Popover.Dropdown>
                <div className="stack" style={{ gap: 6 }}>
                  <b>{t("crossing.purdy")}</b>
                  <span className="sm">{t("crossing.purdyText")}</span>
                </div>
              </Popover.Dropdown>
            </Popover>
          </div>
          <span className="sm mu">{t("crossing.planHelp")}</span>
          <PairMatrix
            mothers={spec.mothers}
            fathers={spec.fathers}
            pairs={spec.pairs}
            onToggle={(m, f) => patch({ pairs: toggledPair(spec.pairs, m, f) })}
          />
        </section>
        <Splitter cssVar="--cr-plan-w" side="start" min={260} max={600} />
        <section className="card panel">
          <div className="hstack panel-head">
            <h2 className="h2">{t("crossing.resulting")}</h2>
            <span className="chip g">{formatNumber(plan?.created ?? 0)}</span>
            {preview.busy && <Loader size="xs" />}
          </div>
          <CrossTable rows={result?.rows ?? plan?.rows ?? []} />
        </section>
      </div>
      {result ? (
        <ResultBanner
          operation={result.operation}
          actions={
            <Button size="xs" variant="default" leftSection={<Icon name="download" size={14} />} onClick={download}>
              {t("crossing.exportList")}
            </Button>
          }
        >
          <b>{t("crossing.done", { count: result.written, ev: ev + 1, kis: spec.target })}</b>
        </ResultBanner>
      ) : (
        <RunBar
          requirements={[
            { label: t("runBar.targetTrial", { ev: ev + 1 }), done: !!spec.target.trim(), field: "cr-target" },
            { label: t("crossing.needTarget"), done: !!spec.aim, field: "cr-aim" },
            { label: t("crossing.needMother"), done: spec.mothers.length > 0 },
            { label: t("crossing.needFather"), done: spec.fathers.length > 0 },
            { label: t("crossing.needPair"), done: !!body && (!plan || plan.created > 0) },
            { label: t("crossing.needYearOpen", { ev: ev + 1 }), done: !plan || plan.season_exists },
          ]}
          summary={
            plan && (
              <span>
                <b>{t("crossing.willCreate", { count: plan.created, ev: ev + 1, kis: spec.target })}</b>
                {codes.length > 0 && ` · ${codes.length > 1 ? `${codes[0]} – ${codes[codes.length - 1]}` : codes[0]}`}
                {` · ${t("crossing.newCombinations", { count: plan.new_lines })}`}
              </span>
            )
          }
          undoable
          busy={preview.busy || !plan}
          run={{
            label: t("crossing.create", { count: plan?.created ?? 0 }),
            loading: run.isPending,
            onClick: () => body && plan && run.mutate({ data: { ...body, gh: spec.gh, pgm: spec.pgm, token: plan.token } }),
          }}
        />
      )}
    </div>
  );
}
